<template>
  <template v-if="card">
    <Wrap>
      <PhotosPreview
        :photos="card.photos"
        :experience="card.galleryExperience"
        :experience-id="card.galleryExperience.id"
        class="photos-preview"
        @hero-load="showBelowFold"
      />
    </Wrap>
    <Header :crumbs="card.breadcrumbs" />
    <Wrap>
      <div class="card-content">
        <!-- h1 задаёт SEO, название достопримечательности остаётся запасным вариантом -->
        <SeoContent
          :heading="seo?.h1 || card.name"
          :description="card.description"
          :info="card.info"
          :sight-title="card.name"
          :sight-address="card.info.address ?? ''"
        />
        <Divider />
        <ExcursionSchedule :items="card.schedule" />
        <ChooseExperienceBanner v-if="isDesktopSmall" class="card-content__banner" v-bind="bannerProps" />
        <template v-else>
          <Divider v-if="card.schedule.length" flush-bottom-on-tablet />
          <!-- обёртка остаётся на месте баннера и схлопывается в ноль, пока вместо него показан прилипающий -->
          <div ref="inlineBannerWrap">
            <ChooseExperienceBannerMobile v-if="!showPinnedBanner" v-bind="bannerProps" />
          </div>
        </template>
        <SightLocation v-if="card.location" class="card-content__location" :location="card.location" />
        <article class="card-content__article">
          <h2 v-if="hasArticleHeading" class="card-content__article-title" v-html="articleHeading" />
          <div class="post-wrap">
            <Description :data="description" :article-title="card.name" :article-url="canonicalUrl" />
          </div>
        </article>
      </div>
      <!--
        на планшете и мобильном баннер сначала стоит в потоке на своём месте, а когда его место
        уезжает за верх экрана, прилипает к низу на всю ширину обёртки, а когда доезжает до конца
        контента, снова становится обычным блоком (маркер за ним показывает, что блок на своём месте)
      -->
      <template v-if="showPinnedBanner">
        <ChooseExperienceBannerMobile :pinned="!isPinnedBannerAtEnd" v-bind="bannerProps" />
        <div ref="pinnedBannerEnd" />
      </template>
    </Wrap>
    <!-- блоки ниже статьи рендерятся (и подгружают свои чанки) только после главного фото -->
    <Wrap v-if="isBelowFoldVisible">
      <ExperienceListingStandalone
        :experiences="experiencesWithFavorite"
        analytic-events-prefix="sight"
        :show-location="true"
        :more-button="showMoreButton"
        :more-button-loading="isLoadingExperiences"
        :more-button-text="t('experiences.showAll')"
        :skeletons-count="6"
        @load-more="handleLoadMore"
        @card-click="handleCardClick"
        @set-favorite="handleSetFavorite"
        @unset-favorite="handleUnsetFavorite"
      />
    </Wrap>
    <Attractions
      v-if="isBelowFoldVisible"
      :attractions="card.nearby.items"
      :title="nearbyTitle"
      :sights-url="card.nearby.url"
    />
    <Wrap v-if="isBelowFoldVisible">
      <FaqSection v-if="card.faq.items.length" :title="card.faq.title" :items="card.faq.items" />
      <SubscriptionBlock api-url="" :analytics-api-url="VITE_RUNTIME_SOGU_SERVICE_URL" />
    </Wrap>
  </template>
  <AttractionPageSkeleton v-else-if="isLoading" />
  <NotFoundBlock v-else-if="hasError" api-url="" :sogu-url="VITE_RUNTIME_SOGU_SERVICE_URL" with-subscription-block />
</template>

<script setup lang="ts">
import { useI18n } from '@experience/i18n';
import { computed, defineAsyncComponent, onBeforeMount } from 'vue';
import { useRoute } from 'vue-router';

import { useExperienceEntity } from '@/entities/experience/entity';
import { getRuntimeConfig } from '@/shared/config/runtime';
import { setSightId, useAnalytics } from '@/shared/lib/analytics';
import { useRenderReport } from '@/shared/lib/lifecycle';
import Divider from '@/shared/ui/Divider.vue';
import Wrap from '@/shared/ui/Wrap.vue';
import { useScreenSize } from '@/shared/utils/breakpoints';
import { PhotosPreview } from '@/widgets/photosPreview';

import AttractionPageSkeleton from './components/AttractionPageSkeleton.vue';
import Description from './components/Description/Description.vue';
import ExcursionSchedule from './components/ExcursionSchedule.vue';
import Header from './components/Header.vue';
import SeoContent from './components/SeoContent.vue';
import { useArticleExperiences } from './composables/useArticleExperiences';
import { useArticleHashScroll } from './composables/useArticleHashScroll';
import { useAttractionPageData } from './composables/useAttractionPageData';
import { useAttractionSeo } from './composables/useAttractionSeo';
import { useBelowFold } from './composables/useBelowFold';
import { usePinnedBanner } from './composables/usePinnedBanner';
import ru from './i18n/ru.json';
import {
  Attractions,
  ExperienceListingStandalone,
  FaqSection,
  NotFoundBlock,
  SightLocation,
  SubscriptionBlock,
} from './lib/lazyBlocks';

const { t } = useI18n<{ message: typeof ru }, 'ru'>({
  inheritLocale: true,
  messages: { ru },
});

const { isDesktopSmall } = useScreenSize();
useRenderReport();

const ChooseExperienceBanner = defineAsyncComponent(() => import('./components/ChooseExperienceBanner.vue'));
const ChooseExperienceBannerMobile = defineAsyncComponent(
  () => import('./components/ChooseExperienceBannerMobile.vue'),
);

const { VITE_RUNTIME_SOGU_SERVICE_URL } = getRuntimeConfig();
const { trackEvent } = useAnalytics();
const route = useRoute();
const experienceEntity = useExperienceEntity();

const pageData = useAttractionPageData(route);
const { data, getAttraction, card, isLoading, hasError, canonicalUrl, description, seo } = pageData;

useAttractionSeo(pageData);

useArticleHashScroll(
  route,
  computed(() => card.value?.id),
);

const {
  experiencesWithFavorite,
  isLoadingExperiences,
  showMoreButton,
  handleLoadMore,
  handleSetFavorite,
  handleUnsetFavorite,
  handleCardClick,
} = useArticleExperiences(data);

const { isBelowFoldVisible, showBelowFold } = useBelowFold(computed(() => card.value?.id));
const { inlineBannerWrap, pinnedBannerEnd, showPinnedBanner, isPinnedBannerAtEnd } = usePinnedBanner(isDesktopSmall);

const articleHeading = computed(() => card.value?.articleTitle);
const hasArticleHeading = computed(() => Boolean(articleHeading.value && description.value?.blocks.length));

const nearbyTitle = computed(() => {
  const cityPhrase = card.value?.nearby.cityPhrase;

  return cityPhrase ? t('nearby.titleInCity', { city: cityPhrase }) : t('nearby.title');
});

const bannerProps = computed(() => ({
  count: card.value?.experiences.count ?? 0,
  href: card.value?.experiences.url ?? '',
  city: card.value?.nearby.cityPhrase,
  emptyHref: card.value?.experiences.cityUrl,
}));

onBeforeMount(async () => {
  // избранное не зависит от карточки, поэтому грузим его параллельно с основным запросом
  void experienceEntity.loadFavorites();
  await getAttraction();

  if (!card.value) {
    return;
  }

  setSightId(card.value.id);
  await trackEvent('sight.pageview', { experiences_count: card.value.experiences.count });
});
</script>

<!--
  Разметку контента рендерят дочерние компоненты articleContent, их узлы не получают
  scope-атрибут страницы, поэтому эти стили подключены вне scoped. Утечки нет: всё живёт
  внутри shadow root веб-компонента.
-->
<style lang="scss">
@import '@/shared/styles/post_page';
</style>

<style lang="scss" scoped>
.photos-preview {
  margin-bottom: 12px;
}

// на десктопе баннер выносится во вторую колонку и идёт параллельно карточке и статье
.card-content {
  display: grid;
  grid-template-columns: minmax(0, 805px) minmax(300px, 1fr);
  column-gap: 40px;

  > * {
    grid-column: 1;
  }

  @include tablet {
    grid-template-columns: minmax(0, 805px);
  }
}

// sticky у grid-элемента ограничен его grid-областью, поэтому растягиваем её на все строки
// контента: строки неявные, из-за чего `-1` тут не работает, а лишние пустые строки
// нулевой высоты на раскладку не влияют — row-gap у грида нет
.card-content__banner {
  position: sticky;
  top: var(--alias-space-24, 24px);
  display: flex;
  flex-direction: column;
  grid-row: 1 / span 100;
  grid-column: 2;
  gap: 24px;
  place-self: start end;
}

// в grid-контейнере вертикальные отступы не схлопываются, поэтому на десктопе,
// где над блоком остаётся только разделитель, отступ уменьшен на его margin
.card-content__location {
  margin-top: 56px;

  @include tablet {
    margin-top: 48px;
  }
}

.card-content__article-title {
  max-width: 805px;
  margin: 56px 0 24px;
  color: var(--alias-color-text-default);

  @include typography.base-800-800-semi;

  @include tablet {
    margin: 56px 0 16px;

    @include typography.base-700-500-semi;
  }

  @include mobile {
    margin: 48px 0 16px;
  }
}

// длинный контент статьи часто выходит за первый экран: отложим его layout/paint до прокрутки
.card-content__article {
  contain-intrinsic-size: 1px 1400px;
  content-visibility: auto;
}
</style>
